import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { WizardStepper } from '../../components/layout/WizardStepper';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { FileUpload } from '../../components/ui/FileUpload';
import { useWizardStore } from '../../store/wizard.store';
import { offersService } from '../../services/offers.service';
import { ocrService } from '../../services/ocr.service';
import { useToast } from '../../components/ui/Toast';
import { Upload, Sparkles, Loader2 } from 'lucide-react';

type AuthorizedPersonFormData = {
  nameEn: string;
  nameAr: string;
  mobile: string;
  idType: string;
  idNumber: string;
};

const WIZARD_STEPS = [
  { label: 'Product', path: '/catalog' },
  { label: 'Location & Coverage', path: '/offers/wizard/location' },
  { label: 'Cart', path: '/offers/wizard/cart' },
  { label: 'Customer', path: '/offers/wizard/customer' },
  { label: 'Auth. Person', path: '/offers/wizard/authorized-person' },
  { label: 'Summary', path: '/offers/wizard/summary' },
];

const ID_TYPES = ['Iqama', 'Saudi ID'];
const SAUDI_MOBILE = /^(\+9665|05)\d{8}$/;

export const AuthorizedPersonStep: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { offerId, setStep, documents, setDocument } = useWizardStore();

  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrPrefilled, setOcrPrefilled] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isSubmitting, isDirty, errors },
  } = useForm<AuthorizedPersonFormData>({
    defaultValues: { nameEn: '', nameAr: '', mobile: '', idType: '', idNumber: '' },
  });

  const prefillDone = useRef(false);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!offerId || prefillDone.current) return;
    offersService.get(offerId).then((offer) => {
      if (isDirtyRef.current) return;
      prefillDone.current = true;
      if (!offer.authorizedPerson) return;
      const ap = offer.authorizedPerson;
      const hasData = Object.values(ap).some((v) => v !== null && v !== '' && v !== undefined);
      if (!hasData) return;
      reset({ nameEn: ap.nameEn ?? '', nameAr: ap.nameAr ?? '', mobile: ap.mobile ?? '', idType: ap.idType ?? '', idNumber: ap.idNumber ?? '' });
    }).catch(() => undefined);
  }, [offerId, reset]);

  if (!offerId) { navigate('/catalog'); return null; }

  const handleLetterChange = async (file: File | null) => {
    setDocument('authorizedPersonLetter', file);
    if (!file) { setOcrPrefilled(false); return; }

    setOcrLoading(true);
    try {
      const result = await ocrService.extractDocument(file, 'AUTH_LETTER', offerId);
      let anyPrefilled = false;
      if (result.nameEn) { setValue('nameEn', result.nameEn, { shouldDirty: true }); anyPrefilled = true; }
      if (result.nameAr) { setValue('nameAr', result.nameAr, { shouldDirty: true }); anyPrefilled = true; }
      if (result.idNumber) { setValue('idNumber', result.idNumber, { shouldDirty: true }); anyPrefilled = true; }
      if (result.idType) { setValue('idType', result.idType, { shouldDirty: true }); anyPrefilled = true; }
      if (anyPrefilled) {
        setOcrPrefilled(true);
        showToast('Fields prefilled from letter — please review', 'success');
      }
    } catch {
      // OCR is best-effort
    } finally {
      setOcrLoading(false);
    }
  };

  const onSubmit = async (data: AuthorizedPersonFormData) => {
    try {
      await offersService.saveAuthorizedPerson(offerId, data);
      setStep(5);
      navigate('/offers/wizard/summary');
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join(', ') : (msg ?? 'Failed to save authorized person'), 'error');
    }
  };

  const handleSaveExit = async () => {
    try { await offersService.saveAuthorizedPerson(offerId, getValues()); } catch { /* best-effort */ }
    navigate('/my-offers');
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Authorized Person" subtitle="Person authorized to sign the contract" />
      <WizardStepper steps={WIZARD_STEPS} currentStep={4} />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ maxWidth: '720px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Authorization Letter FIRST ─────────────────────────────────── */}
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Upload size={15} color="var(--p)" />
              <span className="section-title" style={{ marginBottom: 0 }}>Authorization Letter</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
                Upload first — fields will be prefilled from the letter
              </span>
            </div>
            <div className="card-body">
              <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '16px' }}>
                Upload the official authorization letter. Name and ID fields will be read automatically.
              </p>
              <FileUpload
                label="Authorized Person Letter"
                hint="Official letter — prefills name, ID type, ID number"
                value={documents.authorizedPersonLetter}
                onChange={handleLetterChange}
                required
              />
              <div style={{ marginTop: 6, minHeight: 18 }}>
                {ocrLoading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--p)', fontWeight: 600 }}>
                    <Loader2 size={11} style={{ animation: 'spin .8s linear infinite' }} /> Reading document…
                  </span>
                )}
                {!ocrLoading && ocrPrefilled && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--succ)', fontWeight: 600 }}>
                    <Sparkles size={11} /> Prefilled from letter — review and confirm
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Person details (review & edit prefilled data) ──────────────── */}
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="section-title" style={{ marginBottom: 0 }}>Authorized Person Details</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
                Review and edit before proceeding
              </span>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField label="Full Name (English)" lang="en" required error={errors.nameEn?.message}
                  {...register('nameEn', { required: 'Full name in English is required', pattern: { value: /^[A-Za-z ]+$/, message: 'English letters only' } })} />
                <FormField label="الاسم الكامل" lang="ar" error={errors.nameAr?.message}
                  {...register('nameAr', { pattern: { value: /^[؀-ۿ ]*$/, message: 'Arabic characters only' } })} />
                <FormField label="Mobile" placeholder="+9665XXXXXXXX or 05XXXXXXXX" maxLength={14} required error={errors.mobile?.message}
                  {...register('mobile', { required: 'Mobile number is required', pattern: { value: SAUDI_MOBILE, message: 'Enter a valid Saudi mobile' } })} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label className="lbl">
                    ID Type <span style={{ color: 'var(--err)' }}>*</span>
                  </label>
                  <select
                    className={['inp', errors.idType ? 'err-inp' : ''].filter(Boolean).join(' ')}
                    {...register('idType', { required: 'Please select an ID type' })}
                  >
                    <option value="">Select…</option>
                    {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.idType && <span className="err-msg">{errors.idType.message}</span>}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <FormField label="ID Number" maxLength={10} inputMode="numeric" required error={errors.idNumber?.message}
                    {...register('idNumber', {
                      required: 'ID number is required',
                      validate: { matchesIdType: (value, formValues) => {
                        const type = formValues.idType;
                        if (type === 'Iqama') return /^2\d{9}$/.test(value) || 'Iqama must be 10 digits starting with 2';
                        if (type === 'Saudi ID') return /^1\d{9}$/.test(value) || 'Saudi ID must be 10 digits starting with 1';
                        return /^\d{10}$/.test(value) || 'Must be exactly 10 digits';
                      }},
                    })} />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <Button variant="ghost" type="button" onClick={handleSaveExit}>Save & Exit</Button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="ghost" type="button" onClick={() => navigate('/offers/wizard/customer')}>Back</Button>
              <Button type="submit" loading={isSubmitting}>Next: Order Summary</Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};
